<template>
  <HeroSection 
    title="Nos Tarifs"
    subtitle="Des formules simples et transparentes, adaptées à tous les besoins."
    :isBig="false"
    bgImg="company.jpg"
  />

  <section class="py-12 px-32 bg-white">
    <!-- Tableau comparatif -->
    <div class="w-full mx-auto flex flex-col gap-2">
      <div class="grid md:grid-cols-4 gap-8 h-fit">
        <template
          v-for="(offer, idx) in offers"
          :key="offer.name"
        >
          <div 
            :class="['bg-white rounded-lg shadow p-8 flex flex-col items-center relative', offer.popular ? 'border-4 border-primary' : 'border border-gray-200']"
            style="min-height: 800px; position: relative;"
          >
            <div v-if="offer.popular" class="absolute -top-8 left-1/2 -translate-x-1/2 bg-primary text-white text-xs font-bold px-4 py-1 rounded-full shadow">Le plus populaire</div>
            <!-- Toggle annuel/mensuel -->
            <div class="flex">
              <button
                class="!px-6 !py-2 !m-0 font-bold !rounded-l-lg !rounded-r-none transition flex items-center justify-center"
                :class="offer.selectedPeriod === 'year' ? '!text-white !bg-primary' : 'text-black !bg-gray-300'"
                @click="setPeriod(idx, 'year')"
              >Annuel</button>
              <button
                class="!px-6 !py-2 !m-0 font-bold !rounded-r-lg !rounded-l-none transition flex items-center justify-center"
                :class="offer.selectedPeriod === 'month' ? '!text-white !bg-primary' : 'text-black !bg-gray-300'"
                @click="setPeriod(idx, 'month')"
              >Mensuel</button>
            </div>
            <h2 class="text-xl font-bold mb-2 mt-6">{{ offer.name }}</h2>
            <div class="flex items-end gap-2 mb-2">
              <span class="text-3xl font-bold text-primary">
                {{
                  offer.selectedPeriod === 'year'
                    ? Math.round(offer.priceMonth * 0.8)
                    : offer.priceMonth
                }}€
              </span>
              <span class="text-gray-500">/mois</span>
            </div>
            <div class="mb-2 text-sm text-gray-500">
              <span class="font-bold">{{ offer.selectedPeriod === 'year' ? Math.round(offer.priceMonth * 0.8 * 12) : offer.priceMonth * 12 }}€</span> /an <span class="text-green-600 font-semibold" v-if="offer.selectedPeriod === 'year'">(-20%)</span>
            </div>
            <ul class="mb-6 mt-4 space-y-2 text-gray-700 text-left w-full h-fit">
              <li v-for="feature in offer.features" :key="feature" class="flex items-center gap-2">
                <span class="text-primary">✔</span> {{ feature }}
              </li>
            </ul>
            <div class="absolute left-0 right-0 bottom-0 flex flex-col gap-2 px-8 pb-8">
              <router-link to="/planning" class="bg-primary hover:bg-primary-dark text-white font-bold py-3 px-8 rounded shadow-lg transition">
                Essai gratuit 30 jours
              </router-link>
              <router-link v-if="false"
                :to="{
                  name: 'ShowcasePaymentSummary',
                  query: {
                    nom: offer.name,
                    duree: offer.selectedPeriod,
                    features: JSON.stringify(offer.features),
                    priceMonth: offer.priceMonth,
                    priceId: offer.selectedPeriod === 'year'
                      ? offer.priceIdYear
                      : offer.priceIdMonth,
                  }
                }"
                class="bg-white text-primary font-bold py-3 px-8 rounded shadow-lg border border-primary hover:bg-primary hover:text-white transition"
              >
                Choisir cette formule
              </router-link>
              <span class="text-primary text-sm">Les offres seront très bientôt disponible à l'achat</span>
            </div>
          </div>
        </template>
      </div>
      <span class="w-full text-gray-700 !mt-2">*L'essai gratuit contient les fonctionnalités de la formule Boost</span>
    </div>
  </section>

  <FAQSection :faq="faq" />
</template>

<script>
import HeroSection from '@/components/Showcase/HeroSection.vue';
import FAQSection from '@/components/Showcase/FAQSection.vue';

export default {
  name: 'ShowcasePricing',
  components: {
    HeroSection,
    FAQSection
  },
  data() {
    return {
      offers: [
        {
          name: "Personnel",
          priceMonth: 0,
          features: [
            "Parfait pour les étudiants et les freelances",
            "Accès au planning",
            "Jusqu'à 3 ressources",
            "1 utilisateur",
            "Accès à la trésorerie",
            "Accès à la boîte à idées",
          ],
          popular: false,
          selectedPeriod: 'year',
          priceIdMonth: 'price_1RRCX8CiTPUVpAF4WiGOrht7',
          priceIdYear: 'price_1RRUffCiTPUVpAF46cBynTro'
        },
        {
          name: "Boost",
          priceMonth: 27,
          features: [
            "Parfait pour les artisans et les petits groupes",
            "Accès au planning",
            "Jusqu'à 10 ressources",
            "3 utilisateurs",
            "Accès à la trésorerie",
            "Accès à la boîte à idées",
            "Rappels automatiques",
            "Nombre de projets illimités",
          ],
          popular: true,
          selectedPeriod: 'year',
        },
        {
          name: "Boost +",
          priceMonth: 55,
          features: [
            "Parfait pour les groupes en expansion",
            "Accès au planning",
            "Jusqu'à 15 ressources",
            "10 utilisateurs",
            "Accès à la trésorerie",
            "Accès à la boîte à idées",
            "Rappels automatiques",
            "Nombre de projets illimités",
            "Gestion avancé des utilisateurs"
          ],
          popular: false,
          selectedPeriod: 'year',
        },
        {
          name: "Pionnier",
          priceMonth: 135,
          features: [
            "Parfait pour les groupes bien établis",
            "Accès au planning",
            "Jusqu'à 30 ressources",
            "15 utilisateurs",
            "Accès à la trésorerie",
            "Accès à la boîte à idées",
            "Rappels automatiques",
            "Nombre de projets illimités",
            "Gestion avancé des utilisateurs",
            "Support prioritaire",
            "Participation aux mises à jour",
          ],
          popular: false,
          selectedPeriod: 'year',
        },
      ],
      faq: [
        {
          question: "Puis-je changer de formule plus tard ?",
          answer: "Oui, vous pouvez passer à une formule supérieure ou inférieure à tout moment depuis cette page."
        },
        {
          question: "Des tarifs spéciaux pour les startups ou associations ?",
          answer: "Oui, contactez-nous pour bénéficier d'une offre adaptée à votre structure."
        },
        {
          question: "Quels moyens de paiement acceptez-vous ?",
          answer: "Carte bancaire (Visa, Mastercard), Stripe, PayPal, Apple et Google Pay."
        },
        {
          question: "L'essai gratuit est-il vraiment sans engagement ?",
          answer: "Oui, aucun paiement ni carte bancaire n'est requis pour profiter de l'essai gratuit de 30 jours."
        }
      ]
    };
  },
  methods: {
    setPeriod(idx, period) {
      this.offers[idx].selectedPeriod = period;
    }
  }
};
</script>